import { useState, useEffect, useCallback } from 'react';

const SAVED_THEATERS_KEY = 'ultraman_saved_theaters';
const SESSION_THEATER_KEY = 'ultraman_session_theater';
const MAX_SAVED_THEATERS = 10;

/**
 * Serialize theater state for storage.
 * Strips transient UI fields (fadeState, stackedCards refs) and keeps
 * only the data needed to reconstruct each slide.
 */
function serializeTheater(slides) {
  return slides.map(slide => ({
    id: slide.id,
    caption: slide.caption || '',
    zones: Object.fromEntries(
      Object.entries(slide.zones).map(([zone, cards]) => [
        zone,
        (cards || []).map(card => serializeCard(card)),
      ])
    ),
  }));
}

function serializeCard(card) {
  const out = {
    id: card.id,
    instanceId: card.instanceId,
    name: card.name,
    images: card.images,
    effect: card.effect || undefined,
  };
  if (card.stackedCards && card.stackedCards.length > 0) {
    out.stackedCards = card.stackedCards.map(sc => serializeCard(sc));
  }
  // Preserve any extra fields the card may have (rarity, set, etc.)
  if (card.set) out.set = card.set;
  if (card.number) out.number = card.number;
  if (card.rarity) out.rarity = card.rarity;
  if (card.type) out.type = card.type;
  if (card.color) out.color = card.color;
  return out;
}

/**
 * Custom hook for managing saved theaters in localStorage.
 * Mirrors the useSavedDecks pattern.
 */
export function useSavedTheaters() {
  const [savedTheaters, setSavedTheaters] = useState([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SAVED_THEATERS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSavedTheaters(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.error('Failed to load saved theaters:', err);
      setSavedTheaters([]);
    }
  }, []);

  const persistTheaters = useCallback((theaters) => {
    try {
      localStorage.setItem(SAVED_THEATERS_KEY, JSON.stringify(theaters));
      setSavedTheaters(theaters);
    } catch (err) {
      console.error('Failed to save theaters:', err);
    }
  }, []);

  /**
   * Save theater slides to the saved list
   * @param {{ name: string, slides: Array }} theater
   */
  const saveTheater = useCallback((theater) => {
    const serializedSlides = serializeTheater(theater.slides);
    const totalCards = serializedSlides.reduce((sum, slide) => {
      return sum + Object.values(slide.zones).reduce((zSum, cards) => {
        return zSum + cards.reduce((cSum, card) => {
          return cSum + 1 + (card.stackedCards ? card.stackedCards.length : 0);
        }, 0);
      }, 0);
    }, 0);

    const newTheater = {
      id: Date.now().toString(),
      name: theater.name || 'Untitled Theater',
      slides: serializedSlides,
      slideCount: serializedSlides.length,
      totalCards,
      savedAt: new Date().toISOString(),
    };

    setSavedTheaters(prev => {
      let updated = [newTheater, ...prev];
      if (updated.length > MAX_SAVED_THEATERS) {
        updated = updated.slice(0, MAX_SAVED_THEATERS);
      }
      persistTheaters(updated);
      return updated;
    });

    return true;
  }, [persistTheaters]);

  /**
   * Delete a saved theater by ID
   */
  const deleteTheater = useCallback((theaterId) => {
    setSavedTheaters(prev => {
      const updated = prev.filter(t => t.id !== theaterId);
      persistTheaters(updated);
      return updated;
    });
  }, [persistTheaters]);

  /**
   * Rename a saved theater
   */
  const renameTheater = useCallback((theaterId, newName) => {
    setSavedTheaters(prev => {
      const updated = prev.map(t =>
        t.id === theaterId ? { ...t, name: newName } : t
      );
      persistTheaters(updated);
      return updated;
    });
  }, [persistTheaters]);

  /**
   * Get a saved theater by ID
   */
  const getTheater = useCallback((theaterId) => {
    return savedTheaters.find(t => t.id === theaterId);
  }, [savedTheaters]);

  /**
   * Save current session theater (for tab restore)
   */
  const saveSessionTheater = useCallback((slides) => {
    try {
      if (!slides || slides.length === 0) {
        localStorage.removeItem(SESSION_THEATER_KEY);
        return;
      }
      const sessionData = {
        slides: serializeTheater(slides),
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(SESSION_THEATER_KEY, JSON.stringify(sessionData));
    } catch (err) {
      console.error('Failed to save session theater:', err);
    }
  }, []);

  /**
   * Load session theater (for tab restore)
   */
  const loadSessionTheater = useCallback(() => {
    try {
      const stored = localStorage.getItem(SESSION_THEATER_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('Failed to load session theater:', err);
    }
    return null;
  }, []);

  /**
   * Clear session theater
   */
  const clearSessionTheater = useCallback(() => {
    try {
      localStorage.removeItem(SESSION_THEATER_KEY);
    } catch (err) {
      console.error('Failed to clear session theater:', err);
    }
  }, []);

  return {
    savedTheaters,
    saveTheater,
    deleteTheater,
    renameTheater,
    getTheater,
    saveSessionTheater,
    loadSessionTheater,
    clearSessionTheater,
    maxTheaters: MAX_SAVED_THEATERS,
    canSaveMore: savedTheaters.length < MAX_SAVED_THEATERS,
  };
}

export default useSavedTheaters;
